import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export const SiklusSyukurNeon: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // ==========================================
  // 1. TIMELINE & CAMERA DIRECTING (33.5s = 1005f)
  // ==========================================
  // Beat 1 (0-114f / 0-3.8s): Establishing Shot & Intro
  // Beat 2 (114-225f / 3.8-7.5s): Scooter Rider gazing at Sedan
  // Beat 3 (225-345f / 7.5-11.5s): Sedan Driver gazing at Supercar
  // Beat 4 (345-480f / 11.5-16.0s): Supercar Driver gazing up at Jet in Sky
  // Beat 5 (480-630f / 16.0-21.0s): Pedestrian trudging, gazing at Scooter
  // Beat 6 (630-825f / 21.0-27.5s): Wheelchair Gut-Punch & Leg Focus
  // Beat 7 (825-1005f / 27.5-33.5s): Epic Crane Pull-Out & Closing Quote

  // Camera X (centers subjects accurately in 1080px viewport)
  const camX = interpolate(
    frame,
    [0, 90, 114, 180, 225, 300, 345, 420, 480, 560, 630, 720, 825, 900, 1005],
    [
      0, 0, // Intro overview
      -15, -25, // Scooter & Sedan
      -260, -270, // Sedan & Supercar
      -380, -390, // Supercar & Sky Tilt
      180, 190, // Pedestrian & Scooter
      355, 355, // Wheelchair & Pedestrian Legs (Perfect Framing)
      0, 0, 0, // Epic Wide Panoramic Crane Pull-Out
    ],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // Camera Y
  const camY = interpolate(
    frame,
    [0, 100, 225, 345, 410, 480, 630, 720, 825, 920, 1005],
    [
      0, 0, // Intro (0, 100)
      -20, // Scooter (225)
      280, // Supercar tilt up (345)
      360, // Jet prominent in sky (410)
      -10, // Pedestrian (480)
      -30, // Wheelchair start (630)
      -30, // Wheelchair hold (720)
      20, 20, 20, // Final Wide (825, 920, 1005)
    ],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // Camera Zoom / Scale
  const camScale = interpolate(
    frame,
    [0, 90, 114, 180, 225, 345, 420, 480, 630, 720, 825, 900, 1005],
    [
      1.08, 1.15, // Intro (0, 90)
      1.55, 1.58, // Scooter (114, 180)
      1.5, // Sedan (225)
      1.22, // Supercar (345)
      1.22, // Sky Jet (420)
      1.6, // Pedestrian (480)
      1.75, 1.78, // Wheelchair & Moving Legs (630, 720)
      0.95, 0.95, 0.95, // Epic Panoramic Reveal (825, 900, 1005)
    ],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // Subtle breathing / handheld camera motion
  const breathX = Math.sin(frame * 0.04) * 4;
  const breathY = Math.cos(frame * 0.03) * 3;

  // Wet street reflection opacity & light pulse
  const trafficLightGlow = 0.7 + Math.sin(frame * 0.08) * 0.2;
  const planeLightBlink = Math.sin(frame * 0.4) > 0 ? 1 : 0.1;

  // Walking cycle animation for pedestrian
  const walkPhase = frame * 0.15;
  const leg1Angle = Math.sin(walkPhase) * 22;
  const leg2Angle = Math.sin(walkPhase + Math.PI) * 22;
  const arm1Angle = Math.sin(walkPhase + Math.PI) * 18;
  const arm2Angle = Math.sin(walkPhase) * 18;
  const walkBob = Math.abs(Math.sin(walkPhase)) * 4;

  // Jet flight path
  const planeX = interpolate(
    frame,
    [0, 345, 480, 1005],
    [50, 480, 780, 1380],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  const planeY = 460 + Math.sin(frame * 0.025) * 12;

  // Arc Visibilities
  const showScooterArc = frame >= 114 && frame < 825;
  const showSedanArc = frame >= 225 && frame < 825;
  const showSupercarArc = frame >= 345 && frame < 825;
  const showPedestrianArc = frame >= 480 && frame < 825;
  const showWheelchairArc = frame >= 630;
  const showAllArcs = frame >= 825;

  // Heartbeat pulse effect during wheelchair reveal (frame 630 - 800)
  const isHeartbeat = frame >= 640 && frame < 800;
  const heartbeatScale = isHeartbeat ? 1 + Math.pow(Math.sin((frame - 640) * 0.12), 4) * 0.04 : 1;

  // Subtitle timing based on Whisper segments
  const getSubtitle = () => {
    const t = frame / fps;
    if (t < 3.6) return "Kita sering sibuk membandingkan apa yang ada di depan mata...";
    if (t < 7.1) return "Yang naik motor, pengen punya mobil biar nggak kehujanan...";
    if (t < 10.4) return "Yang punya mobil biasa, iri liat mobil mewah...";
    if (t < 15.0) return "Yang punya mobil mewah, natap langit dan pengen punya jet pribadi...";
    if (t < 20.1) return "Tapi di pinggir jalan... ada pejalan kaki yang cuma berharap bisa punya motor.";
    if (t < 25.1) return "Dan di sudut jalan, ada seorang di kursi roda yang impian terbesarnya cuma satu:";
    if (t < 27.5) return "Bisa melangkah dengan kedua kakinya.";
    if (t < 29.5) return "Berhentilah mengeluh.";
    return "Apa yang kamu keluhkan hari ini, adalah impian terbesar orang lain.";
  };

  const currentSub = getSubtitle();

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#080311",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Audio Playback */}
      <Audio src={staticFile("audio/syukur_vo_mastered.mp3")} volume={1.0} />

      {/* Background Ambience: Deep Night Violet Gradient & Stars */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 90% 65% at 50% 25%, #18092e 0%, #0a0314 65%, #05010a 100%)",
        }}
      />

      {/* Soft Ambient City Glow */}
      <div
        style={{
          position: "absolute",
          top: "40%",
          left: "20%",
          width: "60%",
          height: "30%",
          background: "radial-gradient(circle, rgba(147, 51, 234, 0.12) 0%, rgba(0,0,0,0) 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
        }}
      />

      {/* ======================================================== */}
      {/* CAMERA TRANSFORM CONTAINER (CINEMATIC DYNAMIC VIEWPORT)  */}
      {/* ======================================================== */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: "540px 1150px",
          transform: `scale(${camScale * heartbeatScale}) translate(${camX + breathX}px, ${camY + breathY}px)`,
          transition: "transform 0.05s ease-out",
        }}
      >
        {/* World Coordinates:
            Width: 1400px (Centered around 540)
            Ground line Y = 1200px
            Positions on ground:
              - Traffic Light: X = 1220
              - Supercar: X = 1000
              - Sedan: X = 740
              - Scooter: X = 500
              - Pedestrian: X = 320
              - Wheelchair: X = 130
        */}

        {/* ==================================================== */}
        {/* UPPER SKY: LUXURY PRIVATE JET (HIGHLY PROMINENT & GLOWING) */}
        {/* ==================================================== */}
        <div
          style={{
            position: "absolute",
            left: `${planeX}px`,
            top: `${planeY}px`,
            transform: "scale(1.25)",
            filter: "drop-shadow(0 0 12px rgba(255,255,255,0.6))",
            zIndex: 25,
          }}
        >
          <svg width="320" height="110" viewBox="0 0 320 110" fill="none">
            {/* Engine Thrust Plumes (Cyan / Neon Blue) */}
            <path d="M50 48 L15 50 L50 52 Z" fill="#38bdf8" style={{ filter: "drop-shadow(0 0 6px #38bdf8)" }} />
            <path d="M50 68 L15 70 L50 72 Z" fill="#38bdf8" style={{ filter: "drop-shadow(0 0 6px #38bdf8)" }} />
            
            {/* Contrail / Vapor Trails */}
            <line x1="15" y1="50" x2="-70" y2="50" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeDasharray="6 4" />
            <line x1="15" y1="70" x2="-70" y2="70" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeDasharray="6 4" />

            {/* Sleek Gulfstream-style Private Jet Fuselage */}
            <path
              d="M35 60 Q130 54 240 54 Q285 56 305 60 Q312 62 305 64 Q275 66 220 68 L70 68 L40 73 L30 71 L38 64 Z"
              stroke="#ffffff"
              strokeWidth="2.5"
              fill="rgba(255,255,255,0.06)"
            />
            {/* Cockpit Windshield */}
            <path d="M265 56 Q285 57 296 60 L275 61 Z" stroke="#38bdf8" strokeWidth="1.8" fill="rgba(56, 189, 248, 0.4)" />
            {/* Swept Main Wing with Winglet */}
            <path d="M150 56 L85 22 L105 18 L190 56 Z" stroke="#ffffff" strokeWidth="2.2" fill="rgba(255,255,255,0.08)" />
            <path d="M85 22 L82 12 L88 16 Z" stroke="#ffffff" strokeWidth="2" fill="#ffffff" />

            {/* T-Tail Fin */}
            <path d="M50 64 L22 22 L40 22 L64 64 Z" stroke="#ffffff" strokeWidth="2.2" fill="rgba(255,255,255,0.08)" />
            <path d="M16 22 L46 22" stroke="#ffffff" strokeWidth="2.5" />

            {/* Dual Mounted Rear Jet Engines */}
            <rect x="75" y="44" width="36" height="11" rx="5" stroke="#ffffff" strokeWidth="2" fill="#180a2c" />
            <rect x="75" y="65" width="36" height="11" rx="5" stroke="#ffffff" strokeWidth="2" fill="#180a2c" />

            {/* Glowing Passenger Porthole Windows */}
            {[130, 150, 170, 190, 210, 230].map((wx, i) => (
              <circle key={i} cx={wx} cy="61" r="2.2" fill="#ffffff" style={{ filter: "drop-shadow(0 0 3px #ffffff)" }} />
            ))}

            {/* Red Beacon on Tail & Green Strobe on Wingtip */}
            <circle cx="22" cy="22" r="3.5" fill="#ef4444" opacity={planeLightBlink} style={{ filter: "drop-shadow(0 0 8px #ef4444)" }} />
            <circle cx="82" cy="12" r="3.5" fill="#22c55e" opacity={planeLightBlink} style={{ filter: "drop-shadow(0 0 8px #22c55e)" }} />
          </svg>
        </div>

        {/* ==================================================== */}
        {/* STREET SCENE OBJECTS & CHARACTERS                    */}
        {/* ==================================================== */}
        <div style={{ position: "absolute", left: "-160px", top: "0px", width: "1400px", height: "1920px" }}>
          
          {/* Ground Surface Line */}
          <div
            style={{
              position: "absolute",
              top: "1200px",
              left: "-300px",
              width: "2000px",
              height: "2px",
              background: "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.1) 100%)",
              boxShadow: "0 0 12px rgba(255,255,255,0.5)",
            }}
          />

          {/* 1. TRAFFIC LIGHT POLE (X = 1260) */}
          <div style={{ position: "absolute", left: "1250px", top: "820px" }}>
            <svg width="60" height="380" viewBox="0 0 60 380" fill="none">
              {/* Slender Pole */}
              <line x1="30" y1="0" x2="30" y2="380" stroke="#888888" strokeWidth="3" opacity="0.6" />
              {/* Signal Box */}
              <rect x="16" y="20" width="28" height="75" rx="6" stroke="#ffffff" strokeWidth="2" fill="#120c1f" />
              {/* Red Light */}
              <circle
                cx="30"
                cy="42"
                r="8"
                fill="#ff2a2a"
                style={{
                  filter: `drop-shadow(0 0 ${16 * trafficLightGlow}px #ff2a2a)`,
                  opacity: trafficLightGlow,
                }}
              />
              {/* Yellow Light (Dim) */}
              <circle cx="30" cy="58" r="6" fill="#332200" opacity="0.4" />
              {/* Green Light (Dim) */}
              <circle cx="30" cy="74" r="6" fill="#003311" opacity="0.4" />
            </svg>
          </div>

          {/* 2. LUXURY SUPERCAR (X = 960) */}
          <div
            style={{
              position: "absolute",
              left: "940px",
              top: "1090px",
              filter: "drop-shadow(0 0 7px rgba(255,255,255,0.4))",
            }}
          >
            <svg width="280" height="115" viewBox="0 0 280 115" fill="none">
              {/* Sleek Aerodynamic Body */}
              <path
                d="M5 92 Q30 90 45 68 Q90 60 140 38 Q180 34 220 58 Q250 68 272 82 Q278 86 270 94 L240 94 Q235 72 212 72 Q190 72 185 94 L95 94 Q90 72 68 72 Q45 72 40 94 Z"
                stroke="#ffffff"
                strokeWidth="2.4"
                fill="rgba(255,255,255,0.02)"
              />
              {/* Cockpit / Glass Window */}
              <path
                d="M100 62 L138 42 Q175 38 205 58 L195 65 Z"
                stroke="#ffffff"
                strokeWidth="1.8"
                fill="rgba(255,255,255,0.06)"
              />
              {/* Driver Stick Figure (Looking Up at Sky) */}
              <circle cx="145" cy="50" r="7" stroke="#ffffff" strokeWidth="1.8" />
              <path d="M145 57 L147 68" stroke="#ffffff" strokeWidth="1.8" />
              <path d="M147 62 L158 64" stroke="#ffffff" strokeWidth="1.8" /> {/* Hand to Wheel */}
              {/* Wheels */}
              <circle cx="68" cy="94" r="20" stroke="#ffffff" strokeWidth="2.2" />
              <circle cx="68" cy="94" r="10" stroke="#ffffff" strokeWidth="1.4" />
              <circle cx="212" cy="94" r="20" stroke="#ffffff" strokeWidth="2.2" />
              <circle cx="212" cy="94" r="10" stroke="#ffffff" strokeWidth="1.4" />
              {/* Rear Spoiler */}
              <path d="M10 68 L32 70" stroke="#ffffff" strokeWidth="2.5" />
              <path d="M16 70 L18 84" stroke="#ffffff" strokeWidth="1.8" />
              {/* Taillight Red Glow */}
              <circle cx="10" cy="84" r="4" fill="#ef4444" style={{ filter: "drop-shadow(0 0 8px #ef4444)" }} />
            </svg>
          </div>

          {/* 3. FAMILY SEDAN (X = 680) */}
          <div
            style={{
              position: "absolute",
              left: "670px",
              top: "1075px",
              filter: "drop-shadow(0 0 7px rgba(255,255,255,0.4))",
            }}
          >
            <svg width="250" height="130" viewBox="0 0 250 130" fill="none">
              {/* Boxy Sedan Profile */}
              <path
                d="M8 107 L35 107 Q40 85 62 85 Q85 85 90 107 L168 107 Q172 85 195 85 Q218 85 222 107 L242 107 Q248 100 240 90 L215 82 L185 45 L95 45 L50 82 L12 85 Q4 95 8 107 Z"
                stroke="#ffffff"
                strokeWidth="2.4"
                fill="rgba(255,255,255,0.02)"
              />
              {/* Windows: Rear & Front */}
              <path d="M60 80 L96 52 L135 52 L135 80 Z" stroke="#ffffff" strokeWidth="1.8" />
              <path d="M145 52 L180 52 L206 80 L145 80 Z" stroke="#ffffff" strokeWidth="1.8" />
              {/* Driver Inside */}
              <circle cx="165" cy="62" r="7.5" stroke="#ffffff" strokeWidth="1.8" />
              <path d="M165 70 L166 82" stroke="#ffffff" strokeWidth="1.8" />
              <path d="M166 74 L180 77" stroke="#ffffff" strokeWidth="1.8" />
              {/* Wheels */}
              <circle cx="62" cy="107" r="19" stroke="#ffffff" strokeWidth="2.2" />
              <circle cx="62" cy="107" r="8" stroke="#ffffff" strokeWidth="1.4" />
              <circle cx="195" cy="107" r="19" stroke="#ffffff" strokeWidth="2.2" />
              <circle cx="195" cy="107" r="8" stroke="#ffffff" strokeWidth="1.4" />
              {/* Taillight */}
              <rect x="8" y="90" width="5" height="10" rx="2" fill="#ef4444" style={{ filter: "drop-shadow(0 0 6px #ef4444)" }} />
            </svg>
          </div>

          {/* 4. MOTORCYCLE / SCOOTER RIDER (X = 450) */}
          <div
            style={{
              position: "absolute",
              left: "440px",
              top: "1070px",
              filter: "drop-shadow(0 0 7px rgba(255,255,255,0.45))",
            }}
          >
            <svg width="180" height="135" viewBox="0 0 180 135" fill="none">
              {/* Scooter Frame & Wheels */}
              <circle cx="35" cy="115" r="16" stroke="#ffffff" strokeWidth="2.2" />
              <circle cx="140" cy="115" r="16" stroke="#ffffff" strokeWidth="2.2" />
              <path d="M35 115 L65 95 L105 95 L125 75 L135 70" stroke="#ffffff" strokeWidth="2.2" />
              <path d="M140 115 L125 75" stroke="#ffffff" strokeWidth="2.2" />
              {/* Handlebar */}
              <path d="M128 68 L138 65" stroke="#ffffff" strokeWidth="2.5" />
              {/* Seat */}
              <path d="M60 90 L85 90" stroke="#ffffff" strokeWidth="3" />
              {/* Rider Stick Figure */}
              <circle cx="82" cy="45" r="8.5" stroke="#ffffff" strokeWidth="2" /> {/* Head/Helmet */}
              <path d="M82 54 L85 85" stroke="#ffffff" strokeWidth="2" /> {/* Torso */}
              <path d="M85 85 L98 102 L112 102" stroke="#ffffff" strokeWidth="2" /> {/* Leg & Foot */}
              <path d="M83 62 L110 68 L134 66" stroke="#ffffff" strokeWidth="2" /> {/* Arm to Handlebar */}
              {/* Taillight glow */}
              <circle cx="20" cy="104" r="3.5" fill="#ef4444" style={{ filter: "drop-shadow(0 0 7px #ef4444)" }} />
            </svg>
          </div>

          {/* 5. PEDESTRIAN (WALKING ON FOOT) (X = 280) */}
          <div
            style={{
              position: "absolute",
              left: "280px",
              top: `${1070 - walkBob}px`,
              filter: "drop-shadow(0 0 7px rgba(255,255,255,0.45))",
            }}
          >
            <svg width="100" height="135" viewBox="0 0 100 135" fill="none">
              {/* Head */}
              <circle cx="48" cy="22" r="9" stroke="#ffffff" strokeWidth="2.2" />
              {/* Torso */}
              <path d="M48 31 L48 72" stroke="#ffffff" strokeWidth="2.2" />
              {/* Dynamic Walking Legs */}
              <g style={{ transformOrigin: "48px 72px" }}>
                <line
                  x1="48"
                  y1="72"
                  x2={48 + Math.sin((leg1Angle * Math.PI) / 180) * 52}
                  y2={72 + Math.cos((leg1Angle * Math.PI) / 180) * 52}
                  stroke="#ffffff"
                  strokeWidth="2.2"
                />
                <line
                  x1="48"
                  y1="72"
                  x2={48 + Math.sin((leg2Angle * Math.PI) / 180) * 52}
                  y2={72 + Math.cos((leg2Angle * Math.PI) / 180) * 52}
                  stroke="#ffffff"
                  strokeWidth="2.2"
                />
              </g>
              {/* Dynamic Swinging Arms */}
              <line
                x1="48"
                y1="40"
                x2={48 + Math.sin((arm1Angle * Math.PI) / 180) * 35}
                y2={40 + Math.cos((arm1Angle * Math.PI) / 180) * 35}
                stroke="#ffffff"
                strokeWidth="2.0"
              />
              <line
                x1="48"
                y1="40"
                x2={48 + Math.sin((arm2Angle * Math.PI) / 180) * 35}
                y2={40 + Math.cos((arm2Angle * Math.PI) / 180) * 35}
                stroke="#ffffff"
                strokeWidth="2.0"
              />
            </svg>
          </div>

          {/* 6. PERSON IN WHEELCHAIR (X = 90) */}
          <div
            style={{
              position: "absolute",
              left: "90px",
              top: "1078px",
              filter: "drop-shadow(0 0 8px rgba(192, 132, 252, 0.6))",
            }}
          >
            <svg width="130" height="125" viewBox="0 0 130 125" fill="none">
              {/* Main Wheelchair Wheel with Spokes */}
              <circle cx="50" cy="95" r="24" stroke="#c084fc" strokeWidth="2.4" />
              <circle cx="50" cy="95" r="5" fill="#c084fc" />
              <line x1="26" y1="95" x2="74" y2="95" stroke="#c084fc" strokeWidth="1.2" />
              <line x1="50" y1="71" x2="50" y2="119" stroke="#c084fc" strokeWidth="1.2" />
              <line x1="33" y1="78" x2="67" y2="112" stroke="#c084fc" strokeWidth="1.2" />
              <line x1="33" y1="112" x2="67" y2="78" stroke="#c084fc" strokeWidth="1.2" />
              {/* Front Caster Wheel */}
              <circle cx="92" cy="113" r="6" stroke="#c084fc" strokeWidth="2.0" />
              {/* Chair Frame */}
              <path d="M30 65 L48 95 L84 95 L92 113" stroke="#c084fc" strokeWidth="2.2" />
              <path d="M48 95 L48 65 L76 65" stroke="#c084fc" strokeWidth="2.2" />
              {/* Seated Person */}
              <circle cx="58" cy="36" r="8.5" stroke="#ffffff" strokeWidth="2.2" /> {/* Head */}
              <path d="M58 45 L58 68" stroke="#ffffff" strokeWidth="2.2" /> {/* Torso */}
              <path d="M58 68 L80 68 L84 94" stroke="#ffffff" strokeWidth="2.2" /> {/* Lap & Legs Resting */}
              <path d="M58 52 L68 62 L80 64" stroke="#ffffff" strokeWidth="2.0" /> {/* Hands on lap/wheel */}
            </svg>
          </div>

          {/* ==================================================== */}
          {/* THE ARCS OF ENVY / DESIRE (DYNAMIC NEON DOTTED PATHS) */}
          {/* ==================================================== */}
          <svg
            width="1400"
            height="1250"
            viewBox="0 0 1400 1250"
            fill="none"
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
          >
            {/* Arc 1: Wheelchair (x=150, y=1114) -> Pedestrian's moving legs (x=328, y=1160) */}
            {(showWheelchairArc || showAllArcs) && (
              <path
                d="M 152 1114 Q 240 1080 328 1160"
                stroke="#c084fc"
                strokeWidth="2.4"
                strokeDasharray="6 6"
                strokeDashoffset={-frame * 3}
                style={{ filter: "drop-shadow(0 0 6px #c084fc)" }}
              />
            )}

            {/* Arc 2: Pedestrian (x=330, y=1090) -> Scooter (x=520, y=1130) */}
            {(showPedestrianArc || showAllArcs) && (
              <path
                d="M 330 1090 Q 425 1020 520 1130"
                stroke="#ffffff"
                strokeWidth="2.2"
                strokeDasharray="8 8"
                strokeDashoffset={-frame * 2.5}
                style={{ filter: "drop-shadow(0 0 6px #ffffff)" }}
              />
            )}

            {/* Arc 3: Scooter (x=522, y=1115) -> Sedan (x=760, y=1140) */}
            {(showScooterArc || showAllArcs) && (
              <path
                d="M 522 1115 Q 640 1030 760 1140"
                stroke="#ffffff"
                strokeWidth="2.2"
                strokeDasharray="8 8"
                strokeDashoffset={-frame * 2.5}
                style={{ filter: "drop-shadow(0 0 6px #ffffff)" }}
              />
            )}

            {/* Arc 4: Sedan (x=835, y=1135) -> Supercar (x=1080, y=1150) */}
            {(showSedanArc || showAllArcs) && (
              <path
                d="M 835 1135 Q 960 1040 1080 1150"
                stroke="#ffffff"
                strokeWidth="2.2"
                strokeDasharray="8 8"
                strokeDashoffset={-frame * 2.5}
                style={{ filter: "drop-shadow(0 0 6px #ffffff)" }}
              />
            )}

            {/* Arc 5: Supercar (x=1085, y=1140) -> Airplane in Sky */}
            {(showSupercarArc || showAllArcs) && (
              <path
                d={`M 1085 1140 Q 1020 780 ${planeX + 160} ${planeY + 60}`}
                stroke="#ffffff"
                strokeWidth="2.6"
                strokeDasharray="8 8"
                strokeDashoffset={-frame * 3}
                style={{ filter: "drop-shadow(0 0 8px #ffffff)" }}
              />
            )}
          </svg>

          {/* ==================================================== */}
          {/* WET STREET MIRROR REFLECTION                         */}
          {/* ==================================================== */}
          <div
            style={{
              position: "absolute",
              top: "1202px",
              left: "0px",
              width: "1400px",
              height: "240px",
              overflow: "hidden",
              opacity: 0.38,
              filter: "blur(2.2px)",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0) 100%)",
              pointerEvents: "none",
            }}
          >
            {/* Cloned Supercar Reflection (flipped) */}
            <div style={{ position: "absolute", left: "940px", top: "-5px", transform: "scaleY(-1)" }}>
              <svg width="280" height="115" viewBox="0 0 280 115" fill="none">
                <path
                  d="M5 92 Q30 90 45 68 Q90 60 140 38 Q180 34 220 58 Q250 68 272 82 Q278 86 270 94 L240 94 Q235 72 212 72 Q190 72 185 94 L95 94 Q90 72 68 72 Q45 72 40 94 Z"
                  stroke="#ffffff"
                  strokeWidth="2.4"
                />
                <circle cx="68" cy="94" r="20" stroke="#ffffff" strokeWidth="2.2" />
                <circle cx="212" cy="94" r="20" stroke="#ffffff" strokeWidth="2.2" />
                <circle cx="10" cy="84" r="5" fill="#ef4444" style={{ filter: "drop-shadow(0 0 8px #ef4444)" }} />
              </svg>
            </div>

            {/* Cloned Sedan Reflection (flipped) */}
            <div style={{ position: "absolute", left: "670px", top: "-10px", transform: "scaleY(-1)" }}>
              <svg width="250" height="130" viewBox="0 0 250 130" fill="none">
                <path
                  d="M8 107 L35 107 Q40 85 62 85 Q85 85 90 107 L168 107 Q172 85 195 85 Q218 85 222 107 L242 107 Q248 100 240 90 L215 82 L185 45 L95 45 L50 82 L12 85 Q4 95 8 107 Z"
                  stroke="#ffffff"
                  strokeWidth="2.4"
                />
                <circle cx="62" cy="107" r="19" stroke="#ffffff" strokeWidth="2.2" />
                <circle cx="195" cy="107" r="19" stroke="#ffffff" strokeWidth="2.2" />
                <rect x="8" y="90" width="5" height="10" rx="2" fill="#ef4444" style={{ filter: "drop-shadow(0 0 6px #ef4444)" }} />
              </svg>
            </div>

            {/* Cloned Scooter Reflection (flipped) */}
            <div style={{ position: "absolute", left: "440px", top: "-5px", transform: "scaleY(-1)" }}>
              <svg width="180" height="135" viewBox="0 0 180 135" fill="none">
                <circle cx="35" cy="115" r="16" stroke="#ffffff" strokeWidth="2.2" />
                <circle cx="140" cy="115" r="16" stroke="#ffffff" strokeWidth="2.2" />
                <path d="M35 115 L65 95 L105 95 L125 75 L135 70" stroke="#ffffff" strokeWidth="2.2" />
                <circle cx="20" cy="104" r="4" fill="#ef4444" style={{ filter: "drop-shadow(0 0 7px #ef4444)" }} />
              </svg>
            </div>

            {/* Cloned Pedestrian Legs Reflection (flipped) */}
            <div style={{ position: "absolute", left: "280px", top: "-5px", transform: "scaleY(-1)" }}>
              <svg width="100" height="135" viewBox="0 0 100 135" fill="none">
                <line
                  x1="48"
                  y1="72"
                  x2={48 + Math.sin((leg1Angle * Math.PI) / 180) * 52}
                  y2={72 + Math.cos((leg1Angle * Math.PI) / 180) * 52}
                  stroke="#ffffff"
                  strokeWidth="2.4"
                />
                <line
                  x1="48"
                  y1="72"
                  x2={48 + Math.sin((leg2Angle * Math.PI) / 180) * 52}
                  y2={72 + Math.cos((leg2Angle * Math.PI) / 180) * 52}
                  stroke="#ffffff"
                  strokeWidth="2.4"
                />
              </svg>
            </div>

            {/* Cloned Wheelchair Reflection (flipped) */}
            <div style={{ position: "absolute", left: "90px", top: "-5px", transform: "scaleY(-1)" }}>
              <svg width="130" height="125" viewBox="0 0 130 125" fill="none">
                <circle cx="50" cy="95" r="24" stroke="#c084fc" strokeWidth="2.4" />
                <circle cx="92" cy="113" r="6" stroke="#c084fc" strokeWidth="2.0" />
                <path d="M30 65 L48 95 L84 95 L92 113" stroke="#c084fc" strokeWidth="2.2" />
                <path d="M48 95 L48 65 L76 65" stroke="#c084fc" strokeWidth="2.2" />
              </svg>
            </div>

            {/* Traffic Light Red Puddle Reflection */}
            <div
              style={{
                position: "absolute",
                left: "1250px",
                top: "10px",
                width: "60px",
                height: "140px",
                background: "radial-gradient(ellipse at center, rgba(255, 42, 42, 0.45) 0%, rgba(255,42,42,0) 70%)",
                filter: "blur(6px)",
              }}
            />
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* VIGNETTE & CINEMATIC LETTERBOXING OVERLAY                */}
      {/* ======================================================== */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 60%, rgba(0,0,0,0.7) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Heartbeat Vignette Pulse during wheelchair scene */}
      {isHeartbeat && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle, rgba(192, 132, 252, 0) 50%, rgba(192, 132, 252, 0.15) 100%)",
            opacity: Math.sin((frame - 640) * 0.12) > 0 ? 0.7 : 0.1,
            pointerEvents: "none",
          }}
        />
      )}

      {/* ======================================================== */}
      {/* SUBTITLE & KINETIC TYPOGRAPHY (TIKTOK SAFE ZONE: Y 1460)  */}
      {/* ======================================================== */}
      <div
        style={{
          position: "absolute",
          bottom: "340px",
          left: "80px",
          right: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            background: "rgba(10, 4, 20, 0.72)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            padding: "16px 28px",
            maxWidth: "880px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
          }}
        >
          <span
            style={{
              fontSize: "32px",
              fontWeight: 600,
              color: frame >= 630 && frame < 825 ? "#e9d5ff" : "#ffffff",
              letterSpacing: "-0.01em",
              lineHeight: 1.35,
              textShadow: "0 0 12px rgba(255,255,255,0.4)",
            }}
          >
            {currentSub}
          </span>
        </div>
      </div>

      {/* Top Header Watermark (Clean & Minimalist) */}
      <div
        style={{
          position: "absolute",
          top: "140px",
          left: "0",
          right: "0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          opacity: 0.6,
        }}
      >
        <span
          style={{
            fontSize: "16px",
            fontWeight: 500,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: "#ffffff",
            borderBottom: "1px solid rgba(255,255,255,0.3)",
            paddingBottom: "4px",
          }}
        >
          SIKLUS RASA SYUKUR
        </span>
      </div>
    </AbsoluteFill>
  );
};
